/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { Box, ThemeProvider, createTheme } from '@mui/material';

import FullscreenSpinner from 'modules/common/fullscreenSpinner.component';
import RecurringPayableDetails from './recurringPayableDetails.component';

import { COLOR_PRIMARY, COLOR_SECONDARY, COLOR_WARNING } from 'styles/theme';

import { getAuthorizationSummary, getAuthorizationSummaries } from 'modules/authorizations/store/actions/authorizations.actions';
import { getExpenseCodesByClientId, getVendorDetails } from 'modules/authorizations/store/actions/dataManagement.actions';
import { getClientDetails, setClientId } from 'modules/clients/clients.actions';

import {
  getAllCountries,
  getAllCurrencies,
  getAllEnumerations,
  getAllProvinces,
  getAllSaleTypes,
  getAllWeightTypes
} from 'modules/enumerations/store/enumerationActions';

import { fetchEnumerations } from 'modules/authorizations/utils/fetchDataDetailsService';
import { showToast } from 'modules/layout/layout.actions';
import { EXPENSES_PAYEE_TYPE, LINE_ITEM_STATUS } from 'modules/enumerations/enumerationIds';
import {
  createRecurringPayableLineItem,
  deleteRecurringPayableLineItem,
  getRecurringPayableById,
  updateRecurringPayable,
  updateRecurringPayableLineItem
} from '../store/recurringPayables.action';
import { setRecurringPayableId, getExceptionsByClientId } from 'modules/expenses/store/expenses.actions';
import { Permissions } from 'modules/layout/permissions';
import { hasPermission } from 'modules/layout/permissionService';

const buildRPLineItemRow = ({ recurringPayableId, ...rest } = {}) => ({
  lineItemId: null,
  recurringPayableId,
  lineItemStatusId: LINE_ITEM_STATUS.APPROVED,
  addedBy: 1,
  addedOn: new Date(),
  modifiedBy: 1,
  modifiedOn: new Date(),
  ...rest
});

const theme = createTheme({
  palette: { primary: COLOR_PRIMARY, secondary: COLOR_SECONDARY, warning: COLOR_WARNING }
});

const RecurringPayableDetailsView = props => {
  const {
    match,
    enumerations,
    expenseCodes,
    getRecurringPayableById,
    updateRecurringPayable,
    createRecurringPayableLineItem,
    updateRecurringPayableLineItem,
    deleteRecurringPayableLineItem,
    getAuthorizationSummary,
    getExpenseCodesByClientId,
    getVendorDetails,
    clients,
    authorizationSummaries,
    getAuthorizationSummaries,
    getExceptionsByClientId
  } = props;

  const rpIdParam = (match && match.params && match.params.recurringPayableId) || '0';
  const recurringPayableId = parseInt(rpIdParam, 10);

  const [isLoading, setIsLoading] = useState(false);
  const [rpDetail, setRpDetail] = useState(null);
  const [payeeDetails, setPayeeDetails] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);

  const [banks, setBanks] = useState([]);
  const [addresses, setAddresses] = useState([]);

  const clientsArray = useMemo(() => (Array.isArray(clients) ? clients : Object.values(clients ?? {})), [clients]);
  const [exceptions, setExceptions] = useState([]);

  const normalizedClients = useMemo(
    () =>
      (clientsArray ?? []).map(c => ({
        clientRecordId: c.clientRecordId ?? c.id ?? c.clientId,
        legalName: c.legalName ?? c.clientName ?? c.description ?? '',
        id: c.clientRecordId ?? c.id ?? c.clientId,
        description: c.legalName ?? c.clientName ?? c.description ?? '',
        clientId: c.clientRecordId ?? c.id ?? c.clientId,
        clientName: c.legalName ?? c.clientName ?? c.description ?? ''
      })),
    [clientsArray]
  );

  const normalizeAuthSummaries = useMemo(
    () =>
      (authorizationSummaries || []).map(a => ({
        ...a,
        transfereeName: a.transfereeName || `${a.firstName || ''} ${a.lastName || ''}`.trim()
      })),
    [authorizationSummaries]
  );

  const maskAcct = n => {
    const s = String(n || '');
    const last4 = s.slice(-4);
    return last4 ? `••••${last4}` : '';
  };

  const normalizeVendorBanks = arr =>
    Array.isArray(arr)
      ? arr.map(b => ({
          id: b.bankId,
          displayName: `${b.bankName} ${maskAcct(b.accountNumber)}`.trim(),
          _raw: b
        }))
      : [];

  const normalizeAuthBanks = arr =>
    Array.isArray(arr)
      ? arr.map(b => ({
          id: b.bankId,
          displayName: b.bankNickName,
          _raw: b
        }))
      : [];

  const normalizeAddresses = arr =>
    Array.isArray(arr)
      ? arr.map(a => {
          const line1 = [a.addressLine1, a.addressLine2, a.addressLine3].filter(Boolean).join(' ');
          const line2 = [a.city, a.zipcode].filter(Boolean).join(', ');
          return {
            id: a.addressId,
            lines: [line1, line2].filter(Boolean).join('\n'),
            _raw: a
          };
        })
      : [];

  const onUpdateRP = useCallback(
    async payload => {
      const id = payload?.recurringPayableInformation?.recurringPayableId || payload?.recurringPayableId;
      if (!id) return;
      await updateRecurringPayable(id, payload);
      const refreshed = (await getRecurringPayableById(id)).response;
      setRpDetail(refreshed);
      return { response: refreshed };
    },
    [updateRecurringPayable, getRecurringPayableById]
  );

  const onSaveLineItem = useCallback(
    async item => {
      if (!recurringPayableId) return;
      const hasId = !!item?.lineItemId;
      if (hasId) {
        await updateRecurringPayableLineItem(recurringPayableId, item.lineItemId, item);
      } else {
        await createRecurringPayableLineItem(recurringPayableId, buildRPLineItemRow({ recurringPayableId, ...item }));
      }
      const refreshed = (await getRecurringPayableById(recurringPayableId)).response;
      setRpDetail(refreshed);
      return true;
    },
    [recurringPayableId, createRecurringPayableLineItem, updateRecurringPayableLineItem, getRecurringPayableById]
  );

  const onDeleteLineItem = useCallback(
    async lineItemId => {
      if (!recurringPayableId || !lineItemId) return;
      await deleteRecurringPayableLineItem(recurringPayableId, lineItemId);
      const refreshed = (await getRecurringPayableById(recurringPayableId)).response;
      setRpDetail(refreshed);
      return true;
    },
    [recurringPayableId, deleteRecurringPayableLineItem, getRecurringPayableById]
  );

  const onCopyLineItem = useCallback(
    async lineItemId => {
      if (!recurringPayableId || !lineItemId) return;
      const itemToCopy = Array.isArray(rpDetail?.recurringPayableLineItems)
        ? rpDetail.recurringPayableLineItems.find(li => li.lineItemId === lineItemId)
        : null;
      if (!itemToCopy) return;
      const newItem = { ...itemToCopy, lineItemId: null, lineItemStatusId: LINE_ITEM_STATUS.PENDING };
      await createRecurringPayableLineItem(recurringPayableId, newItem);
      const refreshed = (await getRecurringPayableById(recurringPayableId)).response;
      setRpDetail(refreshed);
    },
    [recurringPayableId, createRecurringPayableLineItem, rpDetail, getRecurringPayableById]
  );

  const updateParentRPAttachments = (newRPAttachments) => {
    setRpDetail((prevDetail) => ({
      ...prevDetail,
      recurringPayableAttachments: newRPAttachments
    }));
  };

  const updateParentRPLineAttachments = (newRPLineAttachments) => {
    setRpDetail((prevDetail) => ({
      ...prevDetail,
      recurringPayableLineAttachments: newRPLineAttachments
    }));
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!Number.isFinite(recurringPayableId) || recurringPayableId <= 0) {
        setRpDetail(null);
        setBanks([]);
        setAddresses([]);
        return;
      }
      props.setRecurringPayableId(recurringPayableId);
      setIsLoading(true);
      try {
        const detail = (await getRecurringPayableById(recurringPayableId)).response;
        if (cancelled) return;
        setRpDetail(detail);
        const exceptions = (await getExceptionsByClientId(detail?.recurringPayableInformation?.clientId)).response || [];
        setExceptions(exceptions);
        if (!authorizationSummaries || authorizationSummaries.length === 0) {
          await getAuthorizationSummaries();
        }

        const payee = detail?.payee;
        if (payee?.payeeTypeId === EXPENSES_PAYEE_TYPE.TRANSFEREE) {
          const authId = payee.authorizationId;
          if (authId) {
            const auth = (await getAuthorizationSummary(authId)).response;
            if (cancelled) return;
            setPayeeDetails(auth);
            setBanks(normalizeAuthBanks(auth?.bankAccounts));
            setAddresses(normalizeAddresses(auth?.addresses));
          } else {
            setPayeeDetails(null);
            setBanks([]);
            setAddresses([]);
          }
        } else if (payee?.payeeTypeId === EXPENSES_PAYEE_TYPE.VENDOR) {
          const vendorId = payee.vendorId;
          if (vendorId) {
            const vendor = (await getVendorDetails(vendorId)).response;
            if (cancelled) return;
            setPayeeDetails(vendor);
            setBanks(normalizeVendorBanks(vendor?.banks));
            setAddresses(normalizeAddresses(vendor?.addresses));
          } else {
            setPayeeDetails(null);
            setBanks([]);
            setAddresses([]);
          }
        } else {
          setPayeeDetails(null);
          setBanks([]);
          setAddresses([]);
        }

        await fetchEnumerations(props);
        const clientRecordId = detail?.recurringPayableInformation?.clientId;
        let expenseCodeClientId = null;
        if (clientRecordId) {
          props.setClientId(clientRecordId);
          const clientResp = await props.getClientDetails(clientRecordId);
          if (cancelled) return;
          setClientDetails(clientResp?.response ?? null);
          expenseCodeClientId = clientResp?.response?.clientDetails?.clientId || clientResp?.response?.clientId || null;
        }
        if (expenseCodeClientId) {
          await getExpenseCodesByClientId(expenseCodeClientId);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [recurringPayableId]);

  const canReadRecurringPayables = hasPermission(Permissions.Finance.RecurringPayables.Read);
  const canEditRecurringPayables = hasPermission(Permissions.Finance.RecurringPayables.Edit);

  if (!canReadRecurringPayables) {
    props.history.push('/');
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <Box>
        {!isLoading && rpDetail && enumerations !== undefined && clients !== undefined && (
          <RecurringPayableDetails
            recurringPayableDetail={rpDetail}
            recurringPayableId={recurringPayableId}
            expenseCodes={expenseCodes}
            enumerations={enumerations.allEnums || []}
            banks={banks}
            addresses={addresses}
            payeeDetails={payeeDetails}
            clientRelocationPolicies={clientDetails?.clientRelocationPolicies ?? []}
            clients={normalizedClients || []}
            onUpdateRecurringPayable={onUpdateRP}
            onSaveLineItem={onSaveLineItem}
            onDeleteLineItem={onDeleteLineItem}
            onCopyLineItem={onCopyLineItem}
            history={props.history}
            location={props.location}
            match={props.match}
            normalizeAuthSummaries={normalizeAuthSummaries}
            updateParentRPAttachments={updateParentRPAttachments}
            updateParentRPLineAttachments={updateParentRPLineAttachments}
            setExceptions={setExceptions}
            exceptions={exceptions}
            isReadOnly={!canEditRecurringPayables}
          />
        )}
        {isLoading && <FullscreenSpinner />}
      </Box>
    </ThemeProvider>
  );
};

const mapStateToProps = state => {
  const { authorizationDetails, expenseCodes, authorizationSummaries } = state.authorizations || {};
  const enumerations = state.enumerations;
  const { clients } = state.clients;

  return {
    authorizationDetails,
    expenseCodes,
    enumerations,
    clients,
    authorizationSummaries
  };
};

export default compose(
  connect(mapStateToProps, {
    getRecurringPayableById,
    updateRecurringPayable,
    createRecurringPayableLineItem,
    updateRecurringPayableLineItem,
    deleteRecurringPayableLineItem,
    getAuthorizationSummary,
    getAllEnumerations,
    getExpenseCodesByClientId,
    getClientDetails,
    setClientId,
    getAllCountries,
    getAllCurrencies,
    getAllProvinces,
    getAllSaleTypes,
    getAllWeightTypes,
    showToast,
    getVendorDetails,
    getAuthorizationSummaries,
    setRecurringPayableId,
    getExceptionsByClientId
  })
)(RecurringPayableDetailsView);
